// Minimal pass-through provider kept for App.tsx compatibility.
// The previous popup/auto-unlock system was replaced by server-enforced
// discount eligibility — this component no longer carries any state.

import { createContext, useContext, useEffect, useMemo } from "react";
import { captureReferralFromUrl } from "@/lib/referral";

type RewardsCtx = {
  // No-op kept for backward compatibility with components that still call it.
  // Real eligibility is enforced server-side now.
  showDiscount: (..._args: unknown[]) => void;
  unlocked: string[];
  refresh: () => void;
};

const Ctx = createContext<RewardsCtx | null>(null);

export const useRewards = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRewards must be used within RewardsProvider");
  return ctx;
};

export const RewardsProvider = ({ children }: { children: React.ReactNode }) => {
  // Capture ?ref=METRO-XXXXXX once on mount so referral attribution survives
  // any subsequent navigation / signup flow.
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  const value = useMemo<RewardsCtx>(
    () => ({ showDiscount: () => {}, unlocked: [], refresh: () => {} }),
    [],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
